import { MoreVert } from "@mui/icons-material"
import CloseIcon from "@mui/icons-material/Close"
import EventNoteIcon from "@mui/icons-material/EventNote"
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff"
import PaymentIcon from "@mui/icons-material/Payment"
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard"
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { useHover } from "@uidotdev/usehooks"
import moment from "moment"
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import {
  ActionReqExecutionModeEnum,
  ActionReqThingTypeMrn,
  ActionReqTypeEnum,
} from "shared/actionReqData"
import { Client } from "shared/client"
import { ConfigurationReservationActionRefNameEnum } from "shared/configurationData"
import { Locker } from "shared/locker"
import { VirtualLockerActionEnum } from "shared/lockerData"
import { LockerDoorReservationSession } from "shared/lockerDoorReservationSession"
import { Reservation } from "shared/reservation"
import {
  ReservationAudEventEnum,
  ReservationAudThingTypeMrn,
} from "shared/reservationAudData"
import {
  formatValueAmount,
  ReservationActionEnum,
  ReservationThingTypeMrn,
} from "shared/reservationData"
import { SharedAccessPatternOsEnum } from "shared/sharedAccessPatternsData"
import { SstDoor } from "shared/sst"
import { Thing } from "shared/thing"
import { ModeContext } from "src/ModeContext"
import { useAppDispatch, useAppSelector } from "src/app/hooks"
import { RootState } from "src/app/store"
import { appConfig } from "src/appConfig"
import { isSst2ReservationVisible } from "src/sst2Status"
import { DepositCodePrintButton } from "src/features/zebraPrint/DepositCodePrintButton"
import { isDepositCodeUsable } from "src/features/zebraPrint/autoPrintQueue"
import { useGetMblsConnectorClientsQuery } from "src/features/mblsConnector/mblsConenctorApi"
import { MblsConnectorClient } from "src/features/mblsConnector/mblsConnector"
import {
  selectCurrentTenant,
  selectMe,
  selectTenantId,
} from "src/features/me/meSlice"
import {
  useCreateThingsMutation,
  useGetSmsDeliveriesByReservationIdQuery,
  useGetThingsQuery,
} from "src/features/things/thingsApi"
import {
  selectClientSelectionDefaultValues,
  selectClientSelectionList,
  selectClientSelectionSearchValues,
  selectMblsReservation,
  selectMblsReservationClientId,
  selectThingById,
  thingsActions,
} from "src/features/things/thingsSlice"
import useClearMblsReservation from "src/hooks/useClearMblsReservation"
import { useKeyPress } from "src/hooks/useKeyPress"
import { useLockerActions } from "src/hooks/useLockerActions"
import useLockerDisplayValues from "src/hooks/useLockerDisplayValues"
import { useReservation } from "src/hooks/useReservation"
import useTenantConfig from "src/hooks/useTenantConfig"
import { v4 as uuidv4 } from "uuid"
import { OsLockerCancelReservationDialog } from "./OsLockerCancelReservationDialog"
import { OsLockerMarkAsDepositedDialog } from "./OsLockerMarkAsDepositedDialog"
import { OsLockerOpenDoorDialog } from "./OsLockerOpenDoorDialog"
import { OsLockerReservationForm } from "./OsLockerReservationForm"
import OsLockerReservationInfo from "./OsLockerReservationInfo"
import OsLockerReservationStepper from "./OsLockerReservationStepper"
import { OsLockerShowAdminCodeDialog } from "./OsLockerShowAdminCodeDialog"
import { OsLockerShowPickupCodeDialog } from "./OsLockerShowPickupCodeDialog"
import { OsReservationAmountEdit } from "./OsReservationAmountEdit"
import { OsReservationAuditTrail } from "./OsReservationAuditTrail"
import { DoorTemperatureSection } from "src/features/temperatureDemo/DoorTemperatureSection"
import { OsReservationClientSelector } from "./OsReservationClientSelector"
import RectangularChip from "./RectangularChip"
import { Sl2Btn } from "./Sl2Btn"
import TraceTooltip from "./TraceTooltip"

export type OsLockerDoorDrawerProps = {
  onClose: (sessionId?: string) => void
  door: SstDoor
  reservation?: Reservation
  locker: Locker
  session?: LockerDoorReservationSession
  modName: string
  isCreateMode?: boolean
}

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
} as const

const eyebrowSx = {
  fontSize: "0.66rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
} as const

const tonalIconBtnSx = {
  color: "rgba(0, 0, 0, 0.55)",
  "&:hover": {
    bgcolor: "rgba(35, 66, 133, 0.06)",
    color: "primary.main",
  },
} as const

const subtleDividerSx = {
  borderColor: "rgba(0, 0, 0, 0.06)",
} as const

export const OsLockerDoorDrawer: FC<OsLockerDoorDrawerProps> = ({
  onClose: inOnClose,
  door,
  reservation: r,
  locker,
  session,
  modName,
  isCreateMode = false,
}) => {
  const { t } = useTranslation()

  const { user } = useAppSelector(selectMe)

  const mode = useContext(ModeContext)

  const tenantId = useAppSelector(selectTenantId)

  const reservation = useAppSelector((s: RootState) =>
    selectThingById(s, r?.id || "-1"),
  ) as Reservation

  // Detect if SST has a reservation on this door that SL2 doesn't know about
  const unmatchedV2Reservation = useMemo(() => {
    if (reservation?.id) return null
    const v2Door = locker?.sst2Locker?.doors?.find(
      (d: any) => d.id === door.door_id,
    )
    return v2Door?.reservations?.find(isSst2ReservationVisible) ?? null
  }, [reservation?.id, locker?.sst2Locker?.doors, door.door_id])

  const {
    openDoor,
    getPackage,
    cancelReservation,
    simulateExpiration,
    simulatePayment,
    refreshReservation,
  } = useLockerActions({
    lockerId: locker?.id || "",
    sstLockerId: locker?.sstLockerId || "",
    sstDoorId: door.door_id,
    sstReservationId: reservation?.sstReservation?._id,
    tenantId,
    reservationId: reservation?.id,
  })

  const dispatch = useAppDispatch()

  const onClose = useCallback(() => {
    inOnClose(session?.id)
    dispatch(thingsActions.setHighlightedReservationId(""))
    dispatch(thingsActions.clearClientSelectionDefaultValues())
    dispatch(thingsActions.clearClientSelectionSearchValues())
  }, [dispatch, inOnClose, session?.id])

  // LOCKER-377: bind the drawer's OWN `onClose`, not the raw `inOnClose`.
  // `useKeyPress` invokes its handler with no arguments, so binding `inOnClose`
  // handed the manager `sessionId === undefined` and Escape closed the drawer
  // while leaving the door held until the reaper. `onClose` passes `session?.id`,
  // exactly as the close button does.
  useKeyPress("Escape", onClose)

  const onOpenDoor = useCallback(
    async (employeeCode?: string, shouldClose?: boolean) => {
      await openDoor(employeeCode)
      if (shouldClose === false) return
      onClose()
    },
    [onClose, openDoor],
  )

  const [createThings] = useCreateThingsMutation()

  const tenant = useAppSelector(selectCurrentTenant)

  const [isProcessing, setIsProcessing] = useState(false)

  const onGetPackage = useCallback(
    async (employeeCode?: string) => {
      setIsProcessing(true)

      await getPackage(employeeCode)
      onClose()
      setIsProcessing(false)
    },
    [getPackage, onClose],
  )

  const [searchParams] = useSearchParams()

  const searchValues = useAppSelector(selectClientSelectionSearchValues)

  // at least one of the search values must have at least 3 characters

  const hasSearchValuesMinChars = useMemo(
    () =>
      Object.values(searchValues).some(
        (v: any) => v && v?.length >= appConfig.minSearchChars,
      ),
    [searchValues],
  )
  const {
    data,
    isLoading: isMblsConnectorClientsLoading,
    isFetching: isMblsConnectorClientsFetching,
    refetch: refetchMblsConnectorClients,
  } = useGetMblsConnectorClientsQuery(
    {
      params: {
        ...searchValues,
        tenantId: tenant?.id,
        id: searchParams.get("clientId") ?? undefined,
      },
    },
    {
      skip: !tenant?.mblsProviderId || !hasSearchValuesMinChars,
    },
  )

  const isMblsConnectorProcessing =
    isMblsConnectorClientsLoading || isMblsConnectorClientsFetching

  const [mblsClients, setMblsClients] = useState<MblsConnectorClient[]>([])

  useEffect(() => {
    if (data) {
      setMblsClients(data.data)
    }
  }, [data])

  useEffect(() => {
    setMblsClients([])
  }, [door.door_id])

  const filteredClients = useAppSelector(selectClientSelectionList)

  const qSl2Clients = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternOsEnum.GetClients,
        "ap.tenantId": tenantId,
        tenantId,
      },
    },
    {
      skip: !tenantId,
    },
  )

  const { data: smsDeliveries } = useGetSmsDeliveriesByReservationIdQuery(
    {
      params: {
        tenantId: tenantId || "",
        reservationId: reservation?.id,
      },
    },
    {
      skip: !tenantId || !reservation?.id,
    },
  )

  const onSelectClient = useCallback(
    (client: Client | MblsConnectorClient) => {
      dispatch(
        thingsActions.updateClientSelectionDefaultValues({
          clientId: client.id,
        }),
      )
    },
    [dispatch],
  )
  const [isCancelling, setIsCancelling] = useState(false)

  const onCancel = useCallback(
    async (employeeCode?: string) => {
      setIsCancelling(true)
      try {
        await cancelReservation(employeeCode)
      } catch (err: any) {
        console.error("Error cancelling reservation", err)
      } finally {
        setIsCancelling(false)
      }
      onClose()
    },
    [cancelReservation, onClose],
  )

  const {
    hasExpired,
    statusColor,
    statusTextColor,
    isCompleted,
    hasDeposited,
    hasPickedUp,
  } = useReservation({
    reservation,
    session,
    door,
    hasDoorLink: !!door.links?.find((l) => l.rel === "reservation"),
  })

  const menuOptions = useMemo(() => {
    const items = [ReservationActionEnum.openDoor]

    if (!isCompleted && reservation?.id) {
      items.push("cancelReservation")
    }

    if (
      !isCompleted &&
      !hasExpired &&
      reservation?.id &&
      !reservation?.sstReservation?.reception_time &&
      !reservation?.sstReservation?.delivery_time
    ) {
      items.push(ReservationActionEnum.markAsDeposited)
    }
    if (!isCompleted && !hasExpired) {
      items.push(ReservationActionEnum.showAdminCode)
    }
    if (!isCompleted && !hasExpired) {
      items.push(ReservationActionEnum.showPickupCode)
    }
    // LOCKER-402: `!isCompleted` added alongside `!hasExpired` to HOLD these two
    // where they are. `useReservation` now suppresses `hasExpired` for a
    // collected parcel, so `!hasExpired` alone would newly offer "simulate
    // expiration" and "simulate payment" on a finished reservation — a widening
    // nobody asked for. Every other item in this block was already written
    // `!isCompleted && !hasExpired`; these two were the outliers.
    if (locker?.isVirtual && reservation?.id && !isCompleted && !hasExpired) {
      items.push(ReservationActionEnum.simulateExpiration)
    }
    if (
      locker?.isVirtual &&
      reservation?.id &&
      !isCompleted &&
      !hasExpired &&
      locker?.sstLocker?.payment
    ) {
      items.push(ReservationActionEnum.simulatePayment)
    }
    if (user?.isSuperAdmin && reservation?.id) {
      items.push("refreshReservation")
    }
    return items
  }, [
    hasExpired,
    isCompleted,
    locker?.isVirtual,
    locker?.sstLocker?.payment,
    reservation?.id,
    reservation?.sstReservation?.delivery_time,
    reservation?.sstReservation?.reception_time,
    user?.isSuperAdmin,
  ])

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleOptionClick = (option: string) => {
    if (option === ReservationActionEnum.openDoor) {
      setIsOpenDoorDialogOpen(true)
    } else if (option === ReservationActionEnum.cancelReservation) {
      setIsCancelDialogOpen(true)
    } else if (option === ReservationActionEnum.markAsDeposited) {
      setIsMarkAsDepositedDialogOpen(true)
    } else if (option === ReservationActionEnum.showAdminCode) {
      setIsShowingAdminCodeDialog(true)
    } else if (option === ReservationActionEnum.showPickupCode) {
      setIsShowingPickupCodeDialog(true)
    } else if (option === ReservationActionEnum.simulateExpiration) {
      simulateExpiration(reservation?.id || "")
    } else if (option === ReservationActionEnum.simulatePayment) {
      setIsSimulatePaymentDialogOpen(true)
    } else if (option === "refreshReservation") {
      refreshReservation(reservation?.id || "")
    }
    setAnchorEl(null)
  }

  const [isOpenDoorDialogOpen, setIsOpenDoorDialogOpen] = useState(false)

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)

  const [isMarkAsDepositedDialogOpen, setIsMarkAsDepositedDialogOpen] =
    useState(false)

  const [isSimulatePaymentDialogOpen, setIsSimulatePaymentDialogOpen] =
    useState(false)

  const [simulatePaymentAmount, setSimulatePaymentAmount] = useState("")

  const [simulatePaymentAmountError, setSimulatePaymentAmountError] =
    useState("")

  const [isSumulatePaymentProcessing, setIsSimulatePaymentProcessing] =
    useState(false)
  const [showAuditTrail, setShowAuditTrail] = useState(false)

  useEffect(() => {
    setShowAuditTrail(false)
  }, [reservation?.id])

  const [isShowingAdminCodeDialog, setIsShowingAdminCodeDialog] =
    useState(false)

  const [isShowingPickupCodeDialog, setIsShowingPickupCodeDialog] =
    useState(false)

  const onShow = useCallback(
    async (employeeCode?: string) => {
      try {
        // create reservation audit entry
        const aud = {
          thingTypeMrn: ReservationAudThingTypeMrn,
          reservationId: reservation?.id,
          tenantId: reservation?.tenantId,
          event: ReservationAudEventEnum.adminCodeShown,
          employeeCode,
          details: {},
        }

        await createThings({
          body: { things: [aud] },
          params: { tenantId },
        }).unwrap()
      } catch (err: any) {
        console.error("Error cancelling reservation", err)
      } finally {
      }
    },
    [createThings, reservation?.id, reservation?.tenantId, tenantId],
  )

  const clientSelectionDefaultValues = useAppSelector(
    selectClientSelectionDefaultValues,
  )

  const mblsReservationClientId = useAppSelector(selectMblsReservationClientId)

  const displayClientSelector = useMemo(() => {
    return (
      isCreateMode &&
      !mblsReservationClientId &&
      !clientSelectionDefaultValues.clientId &&
      hasSearchValuesMinChars
    )
  }, [
    clientSelectionDefaultValues.clientId,
    hasSearchValuesMinChars,
    isCreateMode,
    mblsReservationClientId,
  ])

  const mblsReservation = useAppSelector(selectMblsReservation)

  const { clearMblsReservation } = useClearMblsReservation()

  const onFormSuccess = useCallback(
    (result: { ids: string[]; things: Thing[] }) => {
      const newReservation = result?.things?.find(
        (t: Thing) => t.thingTypeMrn === ReservationThingTypeMrn,
      ) as Reservation

      if (newReservation) {
        dispatch(thingsActions.setSelectedReservationId(newReservation.id))
      }
      if (mblsReservation?.clientId) {
        clearMblsReservation(false)
      }
    },
    [clearMblsReservation, dispatch, mblsReservation?.clientId],
  )

  // isSimulatingCode
  const [isSimulatingCode, setIsSimulatingCode] = useState(false)
  const onSimulateCode = useCallback(
    async (code: string) => {
      try {
        setIsSimulatingCode(true)
        const actionReq = {
          thingTypeMrn: ActionReqThingTypeMrn,
          tenantId,
          idempotencyKey: uuidv4(),
          actionType: ActionReqTypeEnum.TriggerVirtualLockerAction,
          executionMode: ActionReqExecutionModeEnum.sync,
          inputData: {
            lockerId: locker.id,
            lockerAction: VirtualLockerActionEnum.SubmitCode,
            code,
          },
        }
        await createThings({
          body: { things: [actionReq] },
          params: { tenantId },
        }).unwrap()
      } catch (err: any) {
        console.error("Error submitting code", err)
      } finally {
        setIsSimulatingCode(false)
      }
    },
    [createThings, locker.id, tenantId],
  )
  const l = useLockerDisplayValues(locker)

  const configs = useTenantConfig(tenantId)

  const [ref, hovering] = useHover()

  // sanity check
  if (!door) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }
  const mt = ["embedded", "RxPro"].includes(mode)
    ? "0px"
    : `${appConfig.mainAppBarHeight}px`

  return (
    <Drawer data-ui="UI-SL2W-050"
      open={true}
      onClose={onClose}
      anchor="right"
      variant="persistent"
      sx={{
        height: "100vh",
        overflowY: "auto",
      }}
      PaperProps={{
        sx: {
          borderLeft: "1px solid rgba(0, 0, 0, 0.08)",
          backgroundColor: "white",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.15)",
            borderRadius: 3,
          },
          "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
        },
      }}
    >
      <Box sx={{ mt }} />
      <Stack direction="row" spacing={0} alignItems="flex-start">
        {displayClientSelector && (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              height: "100vh",
              overflowY: "auto",
              bgcolor: "#fbfcfe",
              borderColor: "rgba(0, 0, 0, 0.08)",
            }}
          >
            <OsReservationClientSelector
              mblsClients={mblsClients as unknown as MblsConnectorClient[]}
              clients={filteredClients}
              isLoading={isMblsConnectorProcessing}
              onSelectMbslClient={onSelectClient}
              onSelectClient={onSelectClient}
              onRefetchMblsClient={refetchMblsConnectorClients}
              onRefetchClient={qSl2Clients.refetch}
            />
          </Paper>
        )}
        <Paper
          variant="elevation"
          elevation={0}
          sx={{
            width: appConfig.itemPreviewDrawerWidth,
            bgcolor: "transparent",
            px: 2.5,
          }}
        >
          <Box
            sx={{
              mx: -2.5,
              px: 2.5,
              pt: 2.5,
              pb: 2,
              mb: 2.5,
              backgroundColor: "#eef2f8",
              backgroundImage: `
                radial-gradient(620px 360px at 100% 0%, rgba(35, 66, 133, 0.12), transparent 60%),
                radial-gradient(480px 320px at 0% 100%, rgba(148, 210, 194, 0.15), transparent 55%)
              `,
              borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
            }}
          >
            <Stack direction="column" spacing={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography sx={eyebrowSx}>{modName}</Typography>
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      bgcolor: "rgba(0, 0, 0, 0.2)",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                    noWrap
                  >
                    {l.i18n.name}
                  </Typography>
                </Stack>
                <IconButton onClick={onClose} size="small" sx={tonalIconBtnSx}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.25,
                      backgroundImage:
                        "linear-gradient(135deg, rgba(35, 66, 133, 0.18), rgba(148, 210, 194, 0.20))",
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      "& svg": { fontSize: "1.15rem" },
                    }}
                  >
                    <SpaceDashboardIcon />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {`${t("door")}`}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: statusColor,
                      color: statusTextColor,
                      borderRadius: "50%",
                      borderColor: statusTextColor,
                      border: "1px solid",
                      width: 32,
                      height: 32,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      boxShadow: "0 2px 6px rgba(35, 66, 133, 0.10)",
                      ml: 1,
                    }}
                  >
                    {door.door_label}
                  </Box>
                  <Chip
                    label={door.size}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.7)",
                      color: "text.primary",
                      fontWeight: 500,
                      fontSize: "0.72rem",
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                      borderRadius: 1,
                      ml: 0.5,
                    }}
                  />
                </Stack>
                <Box>
                  <IconButton
                    aria-label="more"
                    id="long-button"
                    aria-controls={open ? "long-menu" : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-haspopup="true"
                    onClick={handleClick}
                    size="small"
                    sx={tonalIconBtnSx}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Box>
              </Stack>
            </Stack>
          </Box>
          {reservation?.id ? (
            <Stack direction="column" spacing={2}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent={"space-between"}
                sx={{
                  mb: 2,
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <HistoryToggleOffIcon
                    sx={{ fontSize: "1.2rem", color: "primary.main" }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      letterSpacing: "-0.005em",
                      color: "text.primary",
                    }}
                  >
                    {t("currentReservation")}
                  </Typography>
                  {(locker.sstLocker?.payment || !!locker.sst2Locker?.payment_mode) && !hasPickedUp && (
                    <Stack direction="row" spacing={0} alignItems="center">
                      <Chip
                        ref={ref}
                        variant="outlined"
                        size="small"
                        sx={{
                          ml: 0.5,
                          borderColor: "rgba(35, 66, 133, 0.25)",
                          bgcolor: "rgba(35, 66, 133, 0.04)",
                        }}
                        label={
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ color: "primary.main" }}
                          >
                            <PaymentIcon sx={{ fontSize: "1.1em" }} />
                            <Typography variant="caption" fontWeight={700}>
                              {`${formatCadCurrency(
                                reservation?.amountToCollect,
                              )}`}
                            </Typography>

                            <OsReservationAmountEdit
                              reservation={reservation}
                              visible={hovering}
                            />
                          </Stack>
                        }
                      />
                    </Stack>
                  )}
                </Stack>

                {reservation && reservation?.sstReservation && tenantId && (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{
                      color: "primary.main",
                      bgcolor: "rgba(35, 66, 133, 0.06)",
                      border: "1px solid rgba(35, 66, 133, 0.12)",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <EventNoteIcon
                      sx={{
                        fontSize: "1.05em",
                      }}
                    />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ letterSpacing: "0.02em" }}
                    >
                      {`#${reservation.sstReservation.number}`}
                    </Typography>
                  </Stack>
                )}
              </Stack>
              <OsLockerReservationInfo reservation={reservation} />
              {reservation?.notes && (
                <Card
                  sx={{
                    ...cardSx,
                    p: 1.5,
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                  variant="outlined"
                  elevation={0}
                >
                  <Stack direction="column" spacing={0.75}>
                    <Typography sx={eyebrowSx}>{t("notes")}</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.primary", lineHeight: 1.5 }}
                    >
                      {reservation?.notes}
                    </Typography>
                  </Stack>
                </Card>
              )}
              {reservation?.language && (
                <Card
                  sx={{
                    ...cardSx,
                    p: 1.5,
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                  variant="outlined"
                  elevation={0}
                >
                  <Stack direction="column" spacing={0.75}>
                    <Typography sx={eyebrowSx}>
                      {t("communicationLanguage")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.primary" }}
                    >
                      {t(reservation?.language)}
                    </Typography>
                  </Stack>
                </Card>
              )}
              {!hasPickedUp && !hasDeposited && !hasExpired && (
                <Stack
                  direction="column"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    bgcolor: "rgba(148, 210, 194, 0.18)",
                    border: "1px solid rgba(35, 66, 133, 0.12)",
                    color: "primary.main",
                    px: 1.5,
                    py: 1.5,
                    textAlign: "center",
                    borderRadius: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    className={`uf-delivery-code-container`}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {`${t("depositCode")}`}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      fontFamily={"monospace"}
                    >
                      {reservation.sstReservation?.delivery_code}
                    </Typography>
                  </Stack>
                  <Typography variant="caption">
                    {`${t("depositCodeInfo")}`}
                  </Typography>
                  {locker.isVirtual && (
                    <>
                      {isSimulatingCode ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={15} />
                          <Typography variant="caption">
                            {t("processingCode")}
                          </Typography>
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            textDecoration: "underline",
                          }}
                        >
                          <Typography
                            variant="caption"
                            onClick={() =>
                              onSimulateCode(
                                reservation.sstReservation?.delivery_code || "",
                              )
                            }
                            sx={{
                              cursor: "pointer",
                            }}
                          >
                            {t("simulateCode")}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Stack>
              )}
              {!hasExpired &&
                hasDeposited &&
                !hasPickedUp &&
                locker.isVirtual && (
                  <Stack
                    direction="column"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      bgcolor: "#EBF7F4",
                      color: "primary.main",
                      p: 1,
                      textAlign: "center",
                      borderRadius: "8px",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {`${t("receptionCode")}`}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        fontFamily={"monospace"}
                      >
                        {reservation.sstReservation?.reception_code}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="primary.main">
                      {`${t("receptionCodeInfo")}`}
                    </Typography>
                    {locker.isVirtual && (
                      <>
                        {isSimulatingCode ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <CircularProgress size={15} />
                            <Typography variant="caption">
                              {t("processingCode")}
                            </Typography>
                          </Stack>
                        ) : (
                          <Box
                            sx={{
                              textDecoration: "underline",
                            }}
                          >
                            <Typography
                              variant="caption"
                              onClick={() =>
                                onSimulateCode(
                                  reservation.sstReservation?.reception_code ||
                                    "",
                                )
                              }
                              sx={{
                                cursor: "pointer",
                              }}
                            >
                              {t("simulateCode")}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Stack>
                )}
              {/* LOCKER-522: print the deposit code on a Zebra. The
                  button renders nothing unless the tenant has the
                  DepositCodePrinting feature. */}
              {!hasExpired && isDepositCodeUsable(reservation) && (
                <DepositCodePrintButton
                  tenantId={tenantId}
                  reservation={reservation}
                  lockerName={l.i18n.name}
                  doorLabel={door.door_label}
                />
              )}
              {/* LOCKER-402: `hasExpired` is purely time-based, so a collected
                  parcel tripped it once its original deadline passed. CANCEL /
                  CANCELLED are not `isCompleted`, so they keep the code
                  (LOCKER-391 — the parcel is still behind the door). */}
              {hasExpired && !isCompleted && (
                <Stack
                  direction="column"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    bgcolor: statusColor,
                    color: statusTextColor,
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    px: 1.5,
                    py: 1.5,
                    textAlign: "center",
                    borderRadius: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {`${t("expirationCode")}`}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      fontFamily={"monospace"}
                    >
                      {reservation.sstReservation?.admin_code}
                    </Typography>
                  </Stack>
                  <Typography variant="caption">
                    {`${t("expirationCodeInfo")}`}
                  </Typography>
                  {locker.isVirtual && (
                    <>
                      {isSimulatingCode ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={15} />
                          <Typography variant="caption">
                            {t("processingCode")}
                          </Typography>
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            textDecoration: "underline",
                          }}
                        >
                          <Typography
                            variant="caption"
                            onClick={() =>
                              onSimulateCode(
                                reservation.sstReservation?.admin_code || "",
                              ).then(onClose)
                            }
                            sx={{
                              cursor: "pointer",
                            }}
                          >
                            {t("simulateCode")}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Stack>
              )}
              <Divider sx={subtleDividerSx} />
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="flex-end"
              >
                <Link
                  sx={{
                    cursor: "pointer",
                    textDecoration: "none",
                    color: "primary.main",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => setShowAuditTrail(!showAuditTrail)}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: "0.01em" }}
                  >
                    {showAuditTrail ? t("hideAuditTrail") : t("showAuditTrail")}
                  </Typography>
                </Link>
              </Stack>
              {!showAuditTrail && (
                <OsLockerReservationStepper
                  door={door}
                  reservation={JSON.parse(JSON.stringify(reservation))}
                  session={session}
                />
              )}
              {showAuditTrail && (
                <OsReservationAuditTrail
                  reservationId={reservation.id}
                  migratedFromReservationId={
                    reservation.migratedFromSl2ReservationId
                  }
                />
              )}
              <Box pt={5} />
              {reservation?.sstReservation?.status && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={eyebrowSx}>{t("status")}</Typography>
                  <RectangularChip
                    label={t(
                      `sstReservationStatus.${reservation?.sstReservation?.status}`,
                    )}
                    variant="outlined"
                    size="small"
                  />
                </Stack>
              )}
              <Divider sx={subtleDividerSx} />
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <TraceTooltip
                  text={reservation.traceId || "n/a"}
                  tooltipTitle={
                    <Typography variant="caption">
                      {reservation.id}
                    </Typography>
                  }
                />
                {reservation.updatedAt && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {`${t("updated")} ${moment(reservation.updatedAt).format(
                      "YYYY-MM-DD HH:mm",
                    )}`}
                  </Typography>
                )}
              </Stack>
            </Stack>
          ) : unmatchedV2Reservation ? (
            <Stack direction="column" spacing={2} sx={{ p: 2 }}>
              <Alert severity="warning">
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                  {t("unmatchedReservationTitle")}
                </Typography>
                <Typography variant="body2">
                  {t("unmatchedReservationDescription")}
                </Typography>
                <Stack direction="column" spacing={0.5} sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    {`SST ID: ${unmatchedV2Reservation.id}`}
                  </Typography>
                  <Typography variant="caption">
                    {`${t("status")}: ${unmatchedV2Reservation.status}`}
                  </Typography>
                  {unmatchedV2Reservation.reservation_number && (
                    <Typography variant="caption">
                      {`#${unmatchedV2Reservation.reservation_number}`}
                    </Typography>
                  )}
                </Stack>
              </Alert>
            </Stack>
          ) : isCreateMode ? (
            <OsLockerReservationForm
              locker={locker}
              doorId={door.door_id}
              onClose={onClose}
              // @ts-ignore
              displayNewClientCheckbox={!["embedded", "RxPro"].includes(mode)}
              onSuccess={onFormSuccess}
            />
          ) : null}
          {/* LOCKER-534 — read-only; renders nothing unless the demo is on for this door. */}
          <DoorTemperatureSection
            locker={locker}
            doorId={String(door.door_id)}
            doorLabel={door.door_label ?? String(door.door_number)}
          />
        </Paper>
      </Stack>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: 1.5,
            boxShadow: "0 8px 24px rgba(35, 66, 133, 0.12)",
            overflow: "hidden",
          },
        }}
      >
        {menuOptions.map((option) => (
          <MenuItem
            key={option}
            onClick={() => handleOptionClick(option)}
            sx={{
              color: "primary.main",
              minWidth: 220,
              fontSize: "0.875rem",
              fontWeight: 500,
              py: 1,
              "&:hover": {
                bgcolor: "rgba(35, 66, 133, 0.06)",
              },
            }}
          >
            {t(option)}
          </MenuItem>
        ))}
      </Menu>
      {isOpenDoorDialogOpen && (
        <OsLockerOpenDoorDialog
          onClose={() => setIsOpenDoorDialogOpen(false)}
          onOpenDoor={onOpenDoor}
          onCancel={onCancel}
          employeeCodeRequired={
            configs[
              ConfigurationReservationActionRefNameEnum
                .actionRequireEmployeeCode_openDoor
            ]?.bVal ?? true
          }
        />
      )}
      {isCancelDialogOpen && (
        <OsLockerCancelReservationDialog
          onCancel={onCancel}
          onOpenDoor={onOpenDoor}
          onClose={() => setIsCancelDialogOpen(false)}
          hasDeposited={hasDeposited}
          hasExpired={hasExpired}
          employeeCodeRequired={
            configs[
              ConfigurationReservationActionRefNameEnum
                .actionRequireEmployeeCode_cancelReservation
            ]?.bVal ?? true
          }
          adminCode={reservation?.sstReservation?.admin_code || "n/a"}
        />
      )}
      {isMarkAsDepositedDialogOpen && (
        <OsLockerMarkAsDepositedDialog
          onClose={() => setIsMarkAsDepositedDialogOpen(false)}
          onMarkAsDeposited={onGetPackage}
          employeeCodeRequired={
            configs[
              ConfigurationReservationActionRefNameEnum
                .actionRequireEmployeeCode_markAsDeposited
            ]?.bVal ?? true
          }
        />
      )}
      {isShowingAdminCodeDialog && (
        <OsLockerShowAdminCodeDialog
          onClose={() => setIsShowingAdminCodeDialog(false)}
          adminCode={reservation?.sstReservation?.admin_code || "n/a"}
          onShow={onShow}
          employeeCodeRequired={
            configs[
              ConfigurationReservationActionRefNameEnum
                .actionRequireEmployeeCode_showAdminCode
            ]?.bVal ?? true
          }
        />
      )}
      {isShowingPickupCodeDialog && (
        <OsLockerShowPickupCodeDialog
          onClose={() => setIsShowingPickupCodeDialog(false)}
          pickupCode={reservation?.sstReservation?.reception_code || "n/a"}
        />
      )}
      {isSimulatePaymentDialogOpen && (
        <Dialog data-ui="UI-SL2W-065"
          open={isSimulatePaymentDialogOpen}
          onClose={() => setIsOpenDoorDialogOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {t("simulatePaymentConfirmation")}
          </DialogTitle>
          <Divider />
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t("simulatePaymentConfirmationText")}
            </DialogContentText>
            <TextField
              sx={{
                mt: 3,
              }}
              label={t("amount")}
              variant="outlined"
              fullWidth
              value={simulatePaymentAmount}
              onChange={(e) => {
                setSimulatePaymentAmountError("")

                setSimulatePaymentAmount(
                  formatValueAmount(e.target.value as any),
                )
              }}
              onBlur={() => {
                // check is number
                if (isNaN(parseFloat(simulatePaymentAmount))) {
                  setSimulatePaymentAmountError(t("amountMustBeNumber"))
                }

                // validate
                if (parseFloat(simulatePaymentAmount) < 0) {
                  setSimulatePaymentAmountError(t("amountMustBePositive"))
                } else if (
                  parseFloat(simulatePaymentAmount) >
                  reservation?.amountToCollect
                ) {
                  setSimulatePaymentAmountError(t("amountMustBeLessThanTotal"))
                }
              }}
              helperText={simulatePaymentAmountError}
              error={!!simulatePaymentAmountError}
            />
          </DialogContent>
          <DialogActions>
            <Sl2Btn
              onClick={() => setIsSimulatePaymentDialogOpen(false)}
              color="primary"
            >
              {t("cancel")}
            </Sl2Btn>
            <Sl2Btn
              onClick={async () => {
                try {
                  setIsSimulatePaymentProcessing(true)
                  await simulatePayment(
                    reservation?.id,
                    parseFloat(simulatePaymentAmount),
                  )
                } catch (err: any) {
                  console.error("Error simulating payment", err)
                } finally {
                  setIsSimulatePaymentDialogOpen(false)
                  setIsSimulatePaymentProcessing(false)
                }
              }}
              color="primary"
              autoFocus
              variant="contained"
              disabled={isSumulatePaymentProcessing}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {isSumulatePaymentProcessing && <CircularProgress size={15} />}
                <span>{t("simulatePayment")}</span>
              </Stack>
            </Sl2Btn>
          </DialogActions>
        </Dialog>
      )}
    </Drawer>
  )
}

export default OsLockerDoorDrawer

const formatCadCurrency = (amount: number) => {
  return amount?.toLocaleString("en-CA", { style: "currency", currency: "CAD" })
}
